import type { Meta, ReceivedAction } from 'redux-first-router';
import { actionToPath, pathToAction } from 'redux-first-router';
import queryString from 'query-string';
import * as RouteConstants from '@/constants/routes';
import routesGenerator from './routesGenerator';
import type { Locale } from '@/utils/locale';

type _RouteActionType<
  R extends keyof typeof RouteConstants = keyof typeof RouteConstants,
> = R extends `ROUTE_${string}` ? (typeof RouteConstants)[R] : never;

/**
 * All navigable raw route action types in the app.
 */
export type RouteActionType = Prettify<_RouteActionType>;

/**
 * An action produced for Redux First Router.
 */
export type RouteAction = Prettify<{
  type: _RouteActionType;
  payload: {
    query?: {
      [key: string]: string | undefined;
    };
    locale?: Locale;
    [key: string]: any;
  };
  meta: Meta;
  query?: {
    [key: string]: string | undefined;
  };
}>;

const ROUTE_ACTION_TYPES = (() => {
  const keys = Object.keys(RouteConstants).filter((key) =>
    key.startsWith('ROUTE_')
  );
  return keys.map((key) => RouteConstants[key]) as RouteActionType[];
})();

/**
 * Whether the provided action type is a valid action type for Redux First Router.
 */
export const isRouteActionType = (
  actionType: string
): actionType is RouteActionType => {
  return ROUTE_ACTION_TYPES.includes(actionType as RouteActionType);
};

/**
 * Whether the provided action is an action produced for Redux First Router.
 */
export const isRouteAction = (
  action: ReceivedAction
): action is RouteAction => {
  return isRouteActionType(action.type as RouteActionType);
};

const routesMap = routesGenerator();

export const getPath = (action: ReceivedAction) => {
  let currentLocale = 'en';

  if (typeof window !== 'undefined') {
    const currentRouteAction = pathToAction(
      window.location.pathname,
      routesMap
    );
    currentLocale = currentRouteAction.payload?.locale ?? currentLocale;
  }

  const pathLocale = action.payload?.locale ?? currentLocale;

  // If there is no locale variant of the action, return the path as is.
  if (
    !action.type.includes('-LOCALE') &&
    !isRouteActionType(`${action.type}-LOCALE`)
  ) {
    return actionToPath(action, routesMap, queryString);
  }

  // Handle going from `en` locale to another locale.
  // The `en` locale is the default action as it has a route without the locale included in it.
  // When the locale is changed, we need to update the route to include the new locale.
  if (
    !action.type.includes('-LOCALE') &&
    currentLocale !== 'en' &&
    pathLocale !== 'en'
  ) {
    action.type = `${action.type}-LOCALE` as typeof action.type;
    action.payload.locale = pathLocale;
    return actionToPath({ ...action }, routesMap, queryString);
  }

  // Ensure a locale is set in the action if it's not already set.
  if (action.type.includes('-LOCALE') && !action.payload.locale) {
    action.payload.locale = pathLocale;
  }

  return actionToPath(action, routesMap, queryString);
};

export const getPathSafe = (action: ReceivedAction) => {
  try {
    return getPath(action);
  } catch (error) {
    console.error('Unable to generate path', error);
    return null;
  }
};

export const getCurrentRouteObject = () => {
  const action = pathToAction(window.location.pathname, routesGenerator());
  return routesMap[action.type];
};

export const isAuthenticationNeeded = (routeName: string) =>
  Boolean(routesMap[routeName]?.needsAuthentication);

export const isCacheable = (action: ReceivedAction) =>
  Boolean(routesMap[action.type]?.cacheable);

export type CacheKeyParams = {
  pathname: string;
  query: Record<string, string | undefined>;
};
/**
 * Function to generate a cache key for the route. If not provided, the default cache key will be used.
 * This cache key is used to differentiate between different routes with the same path but different query parameters.
 */
export type RouteCacheKeyFn = (params: CacheKeyParams) => string;

const defaultCacheKey: RouteCacheKeyFn = ({ query, pathname }) => {
  const { page } = query;
  const pageQuery =
    typeof page !== 'undefined' && page !== '1' ? `/page/${page}` : '';

  return `${pathname}${pageQuery}`;
};

export const getCacheIdByRoute = (
  action: ReceivedAction,
  params: CacheKeyParams
) => {
  const cacheKeyFn = routesMap[action.type]?.cacheKey;
  return typeof cacheKeyFn === 'function'
    ? cacheKeyFn(params)
    : defaultCacheKey(params);
};
